import {
  ElementKind,
  IBuilding,
  Baseline,
  IElementID,
  IProductElement,
  OneOfElements,
  OneOfParentElements,
  Project,
  ProjectID,
  IProjectInfo,
  OneOfProjectListElements,
  IElement,
  CreatedElement,
  OneOfChildElements,
} from '@shared/models/project.model';
import { getId, omitUndefined, pick } from '@shared/helpers/object.helpers';
import { getElementCategoryId } from '@shared/helpers/element-category-id.helpers';
import { type SettableElementPropertyValues } from '@shared/helpers/element-property-values.helpers';
import {
  applyPropertyValuesToFactory,
  reportInvalidPropertyValues,
} from './element-property-values.helpers';
import { useMemoDeepEqual } from '@client/hooks/use-utils.hooks';
import {
  clearProjectUpdateQueue,
  fetchProject,
  useProjectStateStore,
} from './project.store';
import { applyProjectChangesWithProposalContext } from '@shared/helpers/actions/apply-project-changes-with-proposal-context.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import type { ProposalId } from '@shared/models/proposals.model';
import type { EnrichScope } from '@shared/helpers/pipeline/enrich-scope.helpers';
import type { BuildProposalAppendActionPatchOptions } from '@shared/helpers/actions/apply-changes-to-proposal.helpers';
import {
  buildRaceRecoveryReset,
  isRaceConditionError,
  projectChangesReplayLands,
} from './project-recovery.helpers';
import {
  captureRaceCondition,
  recordProjectSaveBreadcrumb,
  reportInactiveProposalChanges,
  summarizeChangesForBreadcrumb,
} from './project-race-telemetry.helpers';
import {
  markOpenProjectRevisionUnverified,
  rebaseChangesBaselineIfRevisionUnverified,
  type RevisionBaselineResult,
} from './project-revision-sync.helpers';
import {
  IProjectState,
  IUpdateProjectOptions,
  ProjectsLookup,
} from './project-state.model';
import { CreateProjectPayload } from '@shared/models/project.model';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { requireCreatedProjectDesignPath } from '@shared/helpers/router.helpers';
import { getProductByIdDeprecated } from '@shared/helpers/product.helpers';
import {
  applyVersionMetadata,
  getBuildingLifetime,
  getDuplicateVersionName,
  getNextVersionName,
  getProjectMeta,
  IAddElementOptions,
  sortProjectsByLastUpdated,
  updateElements,
} from '@shared/helpers/project.helpers';
import {
  applyRegenerateIdsOptions,
  createId,
} from '@shared/helpers/id.helpers';
import {
  type ApplyProjectChangesConfirmOptions,
  type ApplyProjectChangesOptions,
  useApplyProjectChanges,
} from './use-apply-project-changes.hook';
import { stampApplyAmplitudeVersionId } from './apply-project-changes-amplitude.helpers';
import {
  getFirstProposalIdForVersion,
  getVersionDeleteNavigation,
  getVersionIdToRestoreAfterFailedAdd,
  getVersionIdToRestoreAfterFailedRemove,
  shouldNavigateAfterVersionRemove,
} from './apply-version-navigation.helpers';
import { isTemporaryElementId } from '@shared/helpers/element.helpers';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import { buildDiscardedRowIdsChangesForElementRemoval } from '@shared/helpers/candidate-discard.helpers';
import { buildRemoveElementsInProposalChanges } from '@shared/helpers/actions/remove-elements-in-proposal.helpers';
import {
  usePromiseSnackbar,
  useDebouncedSnackbar,
  ISnackbarOptions,
} from '@client/hooks/use-snackbar.hooks';
import { getProductsLookup } from '../product';
import {
  IFactoryElement,
  IFactoryProductElement,
  IFactoryVersion,
  OneOfChildFactoryElements,
} from '@shared/models/factory-element.model';
import {
  findElement,
  getAllBuildingVersions,
  getBuilding,
  getBuildingVersionById,
  getParentElement,
  isBuildingVersionElement,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { StoreErrorMessage } from '../store-errors';
import {
  useUIState,
  getSelectedVersion,
  getSelectedElementId,
  getSelectedVersionId,
} from '../ui';
import {
  getSelectedProposalSearchValueFromUrl,
  PROPOSAL_SEARCH_PARAM,
  useSelectedProposalId,
} from '@client/hooks/use-selected-proposal-id.hooks';
import {
  useProjectNavigation,
  useRawProjectParams,
} from '@client/hooks/use-router.hooks';
import { getRememberedProposalForVersion } from '@client/hooks/use-router-query-params.hooks';
import {
  dispatchOptimisticVersionAddCancelled,
  dispatchOptimisticVersionAdded,
} from '@client/events/version-add-transition.events';
import { useUsedProductIds } from '@client/hooks/use-used-product-ids.hook';
import { getRecipeLookup, useRecipes } from '../recipe/recipe.hook';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { getElementNames } from '@shared/helpers/element.helpers';
import amplitudeLog from '@client/amplitude';
import {
  exportProject,
  isProjectExportValidationError,
} from '../../helpers/import.helpers';
import { getSkipProjectExportValidationConfirmOptions } from './ExportProjectValidationWarning';
import { useConfig } from '../../providers/ConfigProvider';
import { getFolders } from '../folder';
import {
  enrichOnWorker,
  enrichOnWorkerCancellable,
  bumpEnrichmentCancellationSequence,
} from '@client/workers/enrich-project';
import {
  resetUndoRedoHistory,
  syncUndoRedoStatesAfterLocalEnrichedCommit,
  updateUndoRedoStates,
} from '@shared/helpers/undo-redo.helpers';
import { persistSkipEnrichmentSnapshot } from './persist-skip-enrichment.helpers';
import { tryCatchAwaitable } from '@shared/helpers/utils.helpers';
import type { EnrichProjectOptions } from '@client/workers/enrich-project';
import { buildEnrichScopeFromChanges } from '@shared/helpers/pipeline/enrich-scope.helpers';
import { getPvgisState } from '@client/store/pvgis';
import { validateFolderList } from '@shared/validation/project-folder.validation';
import { getUser, useIsReadonly } from '@client/hooks/use-user.hooks';
import {
  appendProjectEvents,
  readableProjectEvents,
  withoutBuildingSetupMoves,
} from '@shared/helpers/project-events/project-events.helpers';
import type {
  ProjectEvent,
  ProjectEventActivity,
  ProjectEventActor,
} from '@shared/models/project-event.model';
import { findFreeName } from '@shared/helpers/string.helpers';
import { capitalize } from 'lodash';
import { setElementExpanded } from '@client/hooks/use-expand-elements.hooks';
import { UpdateClientAction } from '../store.model';
import { clamp, getMaxValuesInArray } from '@shared/helpers/math.helpers';
import { Results } from '@shared/models/unit.model';
import {
  useFilterProjectListResults,
  useGetResultsPerGFA,
} from '@features/results/use-results.hooks';
import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { SemiPartial } from '@shared/models/type-helpers.model';
import { Recipe } from '@shared/models/recipe.model';
import { required } from '@shared/helpers/function.helpers';
import { useLogEditProjectDialog } from '@features/projects/EditProject/edit-project.hooks';
import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { useSelectedOrganizationName } from '../organization';
import { getDataTables } from '@features/data-table/store';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  useAsyncErrorHandler,
  useErrorHandler,
} from '@client/hooks/use-error-handler.hooks';
import {
  AMP_ELEMENT_ADD,
  AMP_PROJECT_ARCHIVE,
  AMP_PROJECT_DELETE,
  AMP_PROJECT_LOCK,
  AMP_PROJECT_UNARCHIVE,
  AMP_PROJECT_UNLOCK,
} from '@shared/constants';
import { useProjectDesignGfa } from '@features/design/hooks/storeys.hooks';

type IAddElementClientOptions = IAddElementOptions & {
  /**
   * Forwarded to {@link useApplyProjectChanges} (snackbar, undo, active version, ...).
   * `skipServerUpdate` / `skipEnrichment` belong in the add options, which win over this.
   */
  applyOptions?: ApplyProjectChangesOptions;
  /**
   * Source root element id when this add is a subtree duplicate — used to clone matching
   * proposal actions onto the new ids under a selected proposal (DEV-3265).
   */
  duplicateSubtreeSourceElementId?: string;
  /**
   * Category property values the new element should be born with, so it never renders in the
   * wrong place and jumps once a follow-up write lands (DEV-3507). Values the category does not
   * support are ignored and reported to Sentry — they never fail the add.
   */
  propertyValues?: SettableElementPropertyValues;
};
type UseUpdateElementsFn = <T extends OneOfElements>(
  ...elements: (SemiPartial<T, 'id'> | T | undefined)[]
) => Promise<Project>;

export type ICreateProjectOptions = ISnackbarOptions & {
  duplicate?: boolean;
};

/**
 * Adds a child ({@link IElement} / {@link IProductElement}) inside the selected version.
 * Building versions are added with {@link useAddVersion} instead — they are not children.
 */
export type UseAddElementFn = <T extends OneOfChildFactoryElements>(
  target: OneOfElements,
  defaults: T,
  options?: IAddElementClientOptions,
) => Promise<{ element: CreatedElement<T['kind']>; project: Project }>;

type UseAddStandardElementFn = (
  target: OneOfElements,
  categoryId: ElementCategoryID,
  options?: IAddElementClientOptions,
) => Promise<{ element: IElement; project: Project }>;

type IAddVersionOptions = {
  /**
   * Navigate to the new version (defaults to `true`) as soon as it exists in the local
   * store — not after the server PUT — and select its first proposal.
   */
  navigate?: boolean;
  /**
   * After a local add with {@link IAddVersionOptions.navigate}, arm the proposal overview
   * version switcher to open the name field (`renamingId`). Defaults to `true` so every
   * user-created version starts with its generated name focused and selected.
   */
  hintRename?: boolean;
  /** Derive a unique duplicate name from this source name instead of using `Version N`. */
  duplicateNameBase?: string;
  /** Forwarded to {@link useApplyProjectChanges} (snackbar, amplitude, confirm, ...) */
  applyOptions?: ApplyProjectChangesOptions;
};

export const NEW_VERSION_CREATED_SNACKBAR_MESSAGE = 'New version created.';

/**
 * Outcome of {@link useAddVersion}. Only `'added'` carries the new {@link Baseline}:
 * a dismissed confirm ({@link ApplyProjectChangesOptions.confirm}) yields `'cancelled'` and a
 * rejected or reverted apply yields `'failed'`. Both non-success cases have already been
 * reported by the apply layer, so callers just stop.
 */
export type AddVersionResult =
  | { status: 'added'; element: Baseline; project: Project }
  | { status: 'cancelled' | 'failed'; project: Project };

type UseAddVersionFn = (
  partial?: Partial<IFactoryVersion>,
  options?: IAddVersionOptions,
) => Promise<AddVersionResult>;

type UseAddProductElementFn = (
  target: OneOfElements,
  defaults: IFactoryProductElement,
  isSibling?: boolean,
) => Promise<{ element: IProductElement; project: Project }>;
type IRemoveElementsOptions = {
  showConfirm?: boolean | ApplyProjectChangesConfirmOptions;
  navigate?: boolean;
  skipServerUpdate?: boolean;
  onDeleted?: (name: string, elementId: IElementID) => void;
};

type UseRemoveElementsFn = (...elementIds: IElementID[]) => Promise<Project>;

type IRemoveVersionOptions = Pick<
  IRemoveElementsOptions,
  'showConfirm' | 'navigate'
> & {
  /** Visible switcher selection; authoritative while route/UI-store sync catches up. */
  selectedVersionId?: IElementID;
  /**
   * Left-to-right version switcher ids. Destination after delete is the previous
   * id in this list (or the next id when the first is removed).
   */
  orderedVersionIds?: readonly IElementID[];
  /** Forwarded to {@link useApplyProjectChanges} (amplitude, snackbar, ...) */
  applyOptions?: ApplyProjectChangesOptions;
};

type UseRemoveVersionFn = (
  versionId: IElementID,
  applyOptions?: ApplyProjectChangesOptions,
) => Promise<Project>;

/**
 * legacy Local Storage Key for if UI cost should be shown or not
 * Values for this can be 'true' or 'false' in localstorage (strings)
 */
export const legacyCostDisabledLSKey = 'project_cost_is_disabled';

const projectSelector = (state: IProjectState): Project => state.project;

const projectsLookupSelector = (state: IProjectState): ProjectsLookup =>
  state.projectsLookup;

const publicProjectsLookupSelector = (state: IProjectState): ProjectsLookup =>
  state.publicProjectsLookup;

/**
 * Create a new object including a subset of propetries from an object
 * @param obj
 * @param keys
 * @returns
 */
export function useProjectState<K extends keyof IProjectState>(
  ...keys: K[]
): Pick<IProjectState, K> {
  // Create a memoed selector function to pick properties
  const selector = useMemoDeepEqual(
    () => (o: IProjectState) => (keys.length ? pick(o, ...keys) : o),
    keys,
  );
  return useProjectStateStore(selector);
}

/**
 * Get project without causing rerenders
 * @returns
 */
export const getProject = (): Project =>
  projectSelector(useProjectStateStore.getState());

/**
 * Get project without causing rerenders
 * @returns
 */
export const getProjectsLookup = (): ProjectsLookup =>
  projectsLookupSelector(useProjectStateStore.getState());

/**
 * Get getFetchProject without causing rerenders
 * @returns
 */
export const getFetchProject = (): IProjectState['fetchProject'] =>
  useProjectStateStore.getState().fetchProject;

/**
 * Get latest project and trigger a rerender when it changes
 * @returns
 */
export const useProject = (): Project => useProjectStateStore(projectSelector);

const NO_PROJECT_EVENTS: ProjectEvent[] = [];

/** History entries on the open project, oldest first. */
export const useProjectEvents = (): readonly ProjectEvent[] => {
  const events = useProjectStateStore(
    ({ project }) => project.events ?? NO_PROJECT_EVENTS,
  );
  // Skip events the thread cannot show rather than failing the panel.
  return useMemo(
    () => withoutBuildingSetupMoves(readableProjectEvents(events)),
    [events],
  );
};

export const useProjectLookup = (): ProjectsLookup =>
  useProjectStateStore(projectsLookupSelector);

export const usePublicProjectLookup = (): ProjectsLookup =>
  useProjectStateStore(publicProjectsLookupSelector);

/**
 * Use project name without causing side rerenders
 * @returns
 */
export const useProjectName = (): string | undefined =>
  useProjectStateStore(({ project }) => project.name);

/**
 * Get an array of projects
 * @returns
 */
export const useProjects = (): IProjectInfo[] => {
  const projectsLookup = useProjectLookup();
  return useMemo(() => Object.values(projectsLookup), [projectsLookup]);
};

/** Organization projects saved as templates. They stay out of the overview. */
const isTemplateProject = (project: IProjectInfo): boolean => project.template;

/**
 * Projects for the overview: List, Map, filters, label counts, and bar scales.
 * Templates stay in {@link useProjects} and {@link useProjectTemplates} so
 * Create from template still works, but they are not list rows.
 * @returns Non-template projects from the current organization lookup.
 */
export const useProjectsForOverview = (): IProjectInfo[] => {
  const projectsLookup = useProjectLookup();
  return useMemo(
    () =>
      Object.values(projectsLookup).filter(
        (project) => !isTemplateProject(project),
      ),
    [projectsLookup],
  );
};

/**
 * Get an array of template projects
 * @returns
 */
export const useProjectTemplates = (): IProjectInfo[] => {
  const projectsLookup = useProjectLookup();
  return useMemo(
    () => Object.values(projectsLookup).filter(isTemplateProject),
    [projectsLookup],
  );
};

/**
 * Get an array of public projects (projects saved as template in 'template-projects' organization)
 * @returns
 */
export const usePublicTemplateProjects = (): IProjectInfo[] => {
  const projectsLookup = usePublicProjectLookup();
  return useMemo(() => Object.values(projectsLookup), [projectsLookup]);
};

/**
 * Get current project id. Only triggers rerenders when project id changes
 * @returns
 */
export const useProjectId = (): ProjectID =>
  useProjectStateStore((state) => state.project.id);

/**
 * @deprecated Reads the leftover {@link Project.target} column for the project
 * list. Build, chart and report use {@link useResolvedProjectTarget} /
 * {@link useProjectScopeTarget}.
 * @param id - Project id. If omitted, current project id is used. Otherwise, accepts string or number ids for any project
 * @returns project target (can be undefined or null)
 */
export const useProjectTarget = (id?: number | string): Project['target'] => {
  const currentProjectId = useProjectStateStore((state) => state.project.id);
  const projectInfoMap = useProjectLookup();
  const { isLoading } = useProjectState('isLoading');
  if (id === undefined) return projectInfoMap[currentProjectId]?.target;
  const numId = guards.is.string(id) ? parseInt(id, 10) : id;
  const projectInfo = projectInfoMap[numId];
  if (projectInfo === undefined && !isLoading) {
    return undefined;
  }

  return projectInfo?.target;
};

/**
 * Get building. Will trigger rerender when building changes
 * @returns
 */
export const useBuilding = (): IBuilding =>
  useProjectStateStore(({ project }) => getBuilding(project));

/**
 * Use metadata in building. Will only rerender when metadata changes
 * @returns
 */
export const useBuildingMetadata = (): IBuilding['meta'] =>
  useProjectStateStore(({ project }) => getProjectMeta(project));

/**
 * Get building lifetime. Will trigger rerender when building lifetime changes
 * @returns
 */
export const useBuildingLifetime = (): number =>
  useProjectStateStore(({ project }) => getBuildingLifetime(project));

/**
 * Options shared by every project update regardless of how the next project is
 * produced (snackbar copy + lifecycle callbacks + local-only/effect flags).
 */
type CommonProjectUpdateOptions = ISnackbarOptions &
  Pick<
    IUpdateProjectOptions,
    | 'skipServerUpdate'
    | 'skipEnrichment'
    | 'disableErrorSnackbar'
    | 'onPersisted'
    | 'onOptimisticLocalUpdate'
    | 'onEnriched'
    | 'onEnrichFailed'
    | 'onEnrichmentSuperseded'
    | 'effectsOutsideUndo'
    | 'events'
  >;

/**
 * The single, type-enforced way to describe a project mutation for
 * {@link useUpdateProject}. The discriminant decides whether a 422 race is
 * recoverable, so it can never be forgotten.
 *
 * - `changes`: a local edit expressed as declarative {@link ProjectChange}s.
 *   The hook derives the next project itself from `baseline`, so the changes and
 *   the applied project can never desync — and a 422 race auto-recovers by
 *   rebase-replaying the same changes on freshly fetched server data.
 * - `snapshot`: adopt an already-formed project as-is (incoming socket update or
 *   undo/redo restore). It hard-resets to server truth after a 422 unless the
 *   caller supplies a conflict-aware `rebaseOnRace` callback.
 */
export type ChangesProjectUpdate = CommonProjectUpdateOptions & {
  kind: 'changes';
  /** Project the changes were applied on top of (usually the current store project). */
  baseline: Project;
  /** Declarative edits; re-applied verbatim on fresh data during 422 recovery. */
  changes: ProjectChange[];
  /** Proposal selected while applying, for enrich-scope partitioning + replay. */
  selectedProposalId?: ProposalId;
  /** Proposal-append context, carried through replay. */
  proposalAppend?: BuildProposalAppendActionPatchOptions;
  /** Pre-computed enrich scope; derived from `changes` when omitted. */
  enrichScope?: EnrichScope;
  /**
   * History entries that need the changed project, e.g. the actions an apply
   * added. Recorded together with {@link IUpdateProjectOptions.events}.
   */
  eventsFromChanges?: (
    before: Project,
    after: Project,
  ) => ProjectEventActivity[];
  /**
   * Makes this version the project's active one ({@link applyVersionMetadata}) in the same
   * commit as `changes` — project metadata has no {@link ProjectChange} of its own, and a
   * separate snapshot update would split the edit into two undo steps.
   */
  activeVersionId?: IElementID;
  /**
   * Internal: this update is the one rebase-replay after a 422. A further 422
   * resets instead of replaying again.
   */
  replayOfRace?: boolean;
};

export type SnapshotProjectUpdate = CommonProjectUpdateOptions & {
  kind: 'snapshot';
  /** Fully-formed project to persist/adopt as-is. */
  project: Project;
  /**
   * Reapply this edit to fresh server state after an optimistic-lock race.
   * Return undefined when the same data changed remotely and replay would
   * overwrite a real conflict.
   */
  rebaseOnRace?: (fresh: Project) => Project | undefined;
  /** Recompute everything: no `prevProject` diff, no scope reuse (admin force enrich). */
  forceEnrichment?: boolean;
};

export type ProjectUpdate = ChangesProjectUpdate | SnapshotProjectUpdate;

/**
 * Derives the concrete `(project, options)` a {@link ProjectUpdate} represents.
 * The ONE place declarative changes are turned into a project, guaranteeing the
 * two can never drift apart.
 */
export const normalizeProjectUpdate = (
  update: ProjectUpdate,
  /** Who the passed {@link IUpdateProjectOptions.events} are recorded for. */
  actor: ProjectEventActor | undefined,
  now: Date = new Date(),
): { project: Project; options: ISnackbarOptions & IUpdateProjectOptions } => {
  const withEvents = (
    project: Project,
    derived: readonly ProjectEventActivity[] = [],
  ): Project =>
    actor
      ? appendProjectEvents(
          project,
          [...(update.events ?? []), ...derived],
          actor,
          now,
        )
      : project;
  switch (update.kind) {
    case 'snapshot': {
      const {
        kind: _kind,
        project,
        rebaseOnRace: _rebase,
        ...options
      } = update;
      return { project: withEvents(project), options };
    }
    case 'changes': {
      const {
        kind: _kind,
        baseline,
        changes,
        selectedProposalId,
        proposalAppend,
        enrichScope,
        activeVersionId,
        eventsFromChanges,
        replayOfRace,
        ...common
      } = update;
      const changed = applyProjectChangesWithProposalContext(
        baseline,
        selectedProposalId,
        changes,
        proposalAppend,
        // The failed save already reported misses against the editor's tree.
        // Replaying onto server truth must not report removes the winning write applied.
        replayOfRace ? { replayOfRace: true } : undefined,
      );
      // Metadata rides along after the changes so the new version already exists
      const project = activeVersionId
        ? applyVersionMetadata(changed, activeVersionId)
        : changed;
      return {
        project: withEvents(project, eventsFromChanges?.(baseline, project)),
        options: {
          ...common,
          appliedChanges: changes,
          enrichSelectedProposalId: selectedProposalId,
          proposalAppend,
          enrichScope,
        },
      };
    }
    default:
      return assertUnreachable(update);
  }
};

/**
 * The part of {@link EnrichProjectOptions} that decides how much work the
 * pipeline may reuse: a `prevProject` to diff against and an {@link EnrichScope}
 * limiting which versions/proposals are recomputed.
 *
 * - `forceEnrichment`: no `prevProject`, no scope — everything is recomputed.
 * - explicit `enrichScope`: used as-is.
 * - `appliedChanges`: scope derived from the changes.
 * - otherwise: no scope, so the pipeline falls back to the prev-project diff.
 */
const getEnrichReuseOptions = (
  project: Project,
  options: IUpdateProjectOptions,
): Pick<EnrichProjectOptions, 'prevProject' | 'scope'> => {
  if (options.forceEnrichment) return { prevProject: undefined };
  if (options.enrichScope) return { scope: options.enrichScope };
  if (!options.appliedChanges?.length) return {};

  return {
    scope: buildEnrichScopeFromChanges(project, options.appliedChanges, {
      selectedProposalId: options.enrichSelectedProposalId,
    }),
  };
};

const uiEventActor = (): ProjectEventActor | undefined => {
  const user = getUser();
  return user
    ? { actor_id: user.id, actor_kind: 'user', channel: 'ui' }
    : undefined;
};

export const useUpdateProject = (): ((
  update: ProjectUpdate,
) => Promise<Project>) => {
  const { updateProject, updateProjectLocally } = useProjectState(
    'updateProject',
    'updateProjectLocally',
  );

  const readonly = useIsReadonly();

  const errorHandler = useErrorHandler<Project, Project>();
  const asyncErrorHandler = useAsyncErrorHandler<Project, Project>();
  const enqueueSnackbar = useDebouncedSnackbar();

  const persistProjectUpdate = useCallback(
    async (update: ProjectUpdate): Promise<Project> => {
      // Read-only sessions stop at the permission error below. Adopting a
      // server row here would clear undo and replace the open project first.
      // `current` is synchronous: design snapshot persist applies its
      // optimistic project before the first await (DEV-3888).
      const rebased: RevisionBaselineResult<ProjectUpdate> = readonly
        ? { kind: 'current', update }
        : rebaseChangesBaselineIfRevisionUnverified(update);
      switch (rebased.kind) {
        case 'pending': {
          // Captured before the refetch. A newer edit replaces this object
          // while the GET is in flight and must not be wiped (same rule as
          // 422 recovery).
          const storeAtFetch = getProject();
          const prepared = await rebased.update;
          if (prepared === update || prepared.kind !== 'changes') break;
          if (getProject() !== storeAtFetch) {
            // The check compared this save's baseline, not the edit that
            // replaced it. That edit still needs its own server check.
            markOpenProjectRevisionUnverified();
            return getProject();
          }

          // The target is gone on the server. Fall through to the stale PUT so
          // the 422 path resets and tells the user, instead of adopting a no-op.
          const lands = projectChangesReplayLands(prepared.baseline, {
            changes: prepared.changes,
            selectedProposalId: prepared.selectedProposalId,
            proposalAppend: prepared.proposalAppend,
            activeVersionId: prepared.activeVersionId,
          });
          if (!lands) return persistProjectUpdate(update);

          // The tab missed a newer server row. Undo must restore that row, not
          // the stale local project this edit was built on.
          updateProjectLocally({
            action: UpdateClientAction.Update,
            itemOrId: prepared.baseline,
            options: { isLoading: false, isUpdating: false },
          });
          resetUndoRedoHistory();
          return persistProjectUpdate(prepared);
        }
        case 'current':
          break;
        default:
          return assertUnreachable(rebased);
      }

      const actor = uiEventActor();
      const eventTime = new Date();
      const { project: initialProject, options } = normalizeProjectUpdate(
        update,
        actor,
        eventTime,
      );
      let project = initialProject;
      const prevProject = getProject();

      // Flow context for any later Sentry event (esp. a 422 race capture).
      recordProjectSaveBreadcrumb({
        kind: update.kind,
        projectId: initialProject.id,
        baselineUpdatedAt:
          update.kind === 'changes'
            ? update.baseline.updated_at
            : update.project.updated_at,
        ...(update.kind === 'changes'
          ? summarizeChangesForBreadcrumb(update.changes)
          : {}),
      });
      const errorHandlerOpts = (defaultValue: Project) => ({
        defaultValue,
        logError: options.logError,
        disableSnackbar: options.disableErrorSnackbar,
      });

      if (readonly) {
        return errorHandler(
          new Error('User have no right to update project'),
          errorHandlerOpts(prevProject),
        );
      }

      if (project === prevProject) return project;

      // Single stamp point for {@link Project.effectsOutsideUndo}: every
      // committed snapshot carries the labels of the change that produced it
      // (or none), so they travel with the payload through enrichment and the
      // undo/redo stacks without separate bookkeeping.
      if (project.effectsOutsideUndo !== options.effectsOutsideUndo) {
        project = {
          ...project,
          effectsOutsideUndo: options.effectsOutsideUndo,
        };
      }

      const commitEnrichedLocally = (nextProject: Project): Project =>
        updateProjectLocally({
          action: UpdateClientAction.Update,
          itemOrId: nextProject,
          options: { isLoading: false, isUpdating: false },
        });

      // Reset to server truth + clean up all client write state when a save
      // cannot be replayed. Never overwrites a concurrent edit silently.
      const resetToServerData = buildRaceRecoveryReset({
        clearQueue: clearProjectUpdateQueue,
        resetUndoRedoHistory,
        bumpEnrichmentCancellationSequence,
        commitLocally: commitEnrichedLocally,
        onSnackbar: (message) =>
          enqueueSnackbar(message, { variant: 'warning' }),
      });

      /**
       * Load server truth after a 422. Replayable `changes` and snapshots that
       * still own the store retry once on that row; a replay that does not land
       * and a second 422 reset to server truth.
       * Pass {@link ownedSnapshot} when a newer edit may already have replaced
       * it: that edit is kept, and recovery must not clear its PUT or enrich
       * (DEV-3785). The same object check runs again after the refetch, because
       * an edit can land while that GET is in flight. Skip-enrichment reverts
       * before this runs, so it passes the snapshot only when that revert saw a
       * newer project; the post-fetch check uses the store object from when the
       * GET started.
       */
      const recoverFromRaceCondition = async (
        error: unknown,
        payloadUpdatedAt: string,
        ownedSnapshot?: Project,
      ): Promise<Project> => {
        if (!isRaceConditionError(error)) throw error;

        const initiatingProjectId = initialProject.id;
        const raceContext = {
          projectId: initiatingProjectId,
          kind: update.kind,
          hasAppliedChanges: Boolean(options.appliedChanges?.length),
          payloadUpdatedAt,
        };

        /**
         * Keep the live project when it is no longer {@link baseline}.
         * A newer edit of the same project replaces the object before
         * `updated_at` moves, so id equality is not enough.
         */
        const liveIfSnapshotReplaced = (
          baseline: Project | undefined,
        ): Project | undefined => {
          if (!baseline) return undefined;
          const current = getProject();
          if (current === baseline) return undefined;
          if (current.id !== initiatingProjectId) {
            captureRaceCondition('warning', {
              ...raceContext,
              outcome: 'project_changed',
            });
          }
          return current;
        };

        const kept = liveIfSnapshotReplaced(ownedSnapshot);
        if (kept) return kept;

        // This save still owns the store. Drop sibling PUTs that carry the stale
        // token before the refetch. A newer edit's request was kept above.
        clearProjectUpdateQueue();

        const liveIfProjectChanged = (): Project | undefined => {
          const current = getProject();
          if (current.id === initiatingProjectId) return undefined;
          captureRaceCondition('warning', {
            ...raceContext,
            outcome: 'project_changed',
          });
          return current;
        };

        const skipped = liveIfProjectChanged();
        if (skipped) return skipped;

        // Captured immediately before the await so an edit during the GET is
        // distinct from the snapshot this recovery decided to reload.
        const snapshotAtFetch = getProject();
        const fresh = await fetchProject(initiatingProjectId);
        const keptAfterFetch = liveIfSnapshotReplaced(snapshotAtFetch);
        if (keptAfterFetch) return keptAfterFetch;
        // Recovery impossible (no server truth to load) — still capture the
        // race so the hardest-to-diagnose case leaves a breadcrumb trail.
        if (!fresh) {
          captureRaceCondition('error', {
            ...raceContext,
            outcome: 'refetch_failed',
          });
          throw error;
        }

        const canReplay =
          update.kind === 'changes' &&
          !update.replayOfRace &&
          projectChangesReplayLands(fresh, {
            changes: update.changes,
            selectedProposalId: update.selectedProposalId,
            proposalAppend: update.proposalAppend,
            activeVersionId: update.activeVersionId,
          });
        if (canReplay) {
          // The failed attempt's undo frame points at the stale base. The replay
          // records one frame from the server row that won the lock.
          resetUndoRedoHistory();
          bumpEnrichmentCancellationSequence();
          commitEnrichedLocally(fresh);
          // The failed attempt already ran optimistic UI (undo snackbar, navigation).
          // Replaying those callbacks shows a second Undo and can navigate again.
          // onEnrichmentSuperseded stays so a later reset can close that snackbar
          // without useAddVersion's onEnrichFailed URL restore.
          return persistProjectUpdate({
            ...update,
            baseline: fresh,
            replayOfRace: true,
            onOptimisticLocalUpdate: undefined,
            onEnriched: undefined,
            onEnrichFailed: undefined,
          });
        }

        // Close the failed attempt's undo snackbar. onEnrichFailed also restores
        // version-add navigation, which would fight a replay that kept the version.
        options.onEnrichmentSuperseded?.();
        const reset = resetToServerData(fresh);
        captureRaceCondition('error', { ...raceContext, outcome: 'reset' });
        return reset;
      };

      const captureReplayedSave = (saved: Project): Project => {
        if (update.kind !== 'changes' || !update.replayOfRace) return saved;
        captureRaceCondition('warning', {
          projectId: saved.id,
          kind: update.kind,
          hasAppliedChanges: Boolean(options.appliedChanges?.length),
          payloadUpdatedAt: saved.updated_at,
          outcome: 'replayed',
        });
        return saved;
      };

      /**
       * Retry a replayable snapshot once on top of fresh server state. The
       * original optimistic commit already owns the undo frame, so replay
       * updates the store without recording the same edit a second time.
       */
      const replaySnapshotAfterRace = async (
        error: unknown,
        failedSnapshot: Project,
        serverPutOptions: IUpdateProjectOptions,
        resetOnConflict = true,
      ): Promise<Project | undefined> => {
        if (!isRaceConditionError(error)) return undefined;
        if (update.kind !== 'snapshot' || !update.rebaseOnRace)
          return undefined;
        if (getProject() !== failedSnapshot) return getProject();

        const fresh = await fetchProject(initialProject.id);
        if (getProject() !== failedSnapshot) return getProject();
        const rebased = update.rebaseOnRace(fresh);
        if (!rebased) {
          if (!resetOnConflict) return undefined;
          const reset = resetToServerData(fresh);
          captureRaceCondition('error', {
            projectId: initialProject.id,
            kind: update.kind,
            payloadUpdatedAt: failedSnapshot.updated_at,
            outcome: 'reset',
            reason: 'replay_conflict',
          });
          return reset;
        }

        const replayWithEvents = actor
          ? appendProjectEvents(rebased, update.events ?? [], actor, eventTime)
          : rebased;
        let replayCommitted: Project;
        if (options.skipEnrichment && replayWithEvents.enriched) {
          replayCommitted = updateProjectLocally({
            action: UpdateClientAction.Update,
            itemOrId: replayWithEvents,
            options: { isLoading: true, isUpdating: true },
          });
        } else {
          const replayOptimistic = updateProjectLocally({
            action: UpdateClientAction.Update,
            itemOrId: { ...replayWithEvents, enriched: false },
            options: { isLoading: true, isUpdating: true },
          });
          const replayEnrich = await tryCatchAwaitable(
            enrichOnWorkerCancellable(replayOptimistic, {
              ...getEnrichmentOptions(),
              prevProject: fresh,
            }),
          );
          if (getProject() !== replayOptimistic) return getProject();
          if (!replayEnrich.ok || !replayEnrich.value) {
            resetToServerData(fresh);
            if (!replayEnrich.ok) throw replayEnrich.error;
            return fresh;
          }

          replayEnrich.value.updated_at = fresh.updated_at;
          reportInactiveProposalChanges(replayEnrich.value);
          replayCommitted = updateProjectLocally({
            action: UpdateClientAction.Update,
            itemOrId: replayEnrich.value,
            options: { isLoading: true, isUpdating: true },
          });
        }

        try {
          const persisted = await updateProject(
            replayCommitted,
            serverPutOptions,
          );
          captureRaceCondition('warning', {
            projectId: initialProject.id,
            kind: update.kind,
            payloadUpdatedAt: failedSnapshot.updated_at,
            outcome: 'replayed',
          });
          return persisted;
        } catch (retryError) {
          return recoverFromRaceCondition(
            retryError,
            replayCommitted.updated_at,
            replayCommitted,
          );
        }
      };

      // Pre-enriched snapshots (e.g. undo/redo) — skip worker entirely.
      // Only honored when the snapshot really is enriched: a metadata-only
      // change (e.g. import draft write) built on an optimistic unenriched
      // base must go through enrichment, otherwise it would persist an
      // unenriched project (server rejects with 400) and cancel the in-flight
      // worker, leaving the store unenriched forever (DEV-3457).
      if (options.skipEnrichment && project.enriched) {
        return asyncErrorHandler(async () => {
          // Abort cooperative enrich checkpoints (e.g. user undid while worker ran).
          bumpEnrichmentCancellationSequence();
          const selectedVersionId = getSelectedVersionId();
          const skipEnrichmentPutOptions = {
            ...pick(
              options,
              'skipServerUpdate',
              'skipEnrichment',
              'onPersisted',
            ),
            serverOnly: true as const,
          };
          const written = updateProjectLocally({
            action: UpdateClientAction.Update,
            itemOrId: project,
            options: {
              isLoading: !options.skipServerUpdate,
              isUpdating: !options.skipServerUpdate,
            },
          });
          // Seen before revertStore snaps an owned failure back to prevProject.
          // After that snap, the store is no longer `written` either way.
          let newerEditOwnsStore = false;
          try {
            return captureReplayedSave(
              await persistSkipEnrichmentSnapshot({
                prevProject,
                written,
                selectedVersionId,
                persist: () => updateProject(written, skipEnrichmentPutOptions),
                recover: (error) =>
                  replaySnapshotAfterRace(
                    error,
                    written,
                    skipEnrichmentPutOptions,
                    false,
                  ),
                revertStore: (restored) => {
                  // A concurrent snapshot may already own the store; do not wipe it.
                  if (getProject() !== written) {
                    newerEditOwnsStore = true;
                    return;
                  }
                  updateProjectLocally({
                    action: UpdateClientAction.Update,
                    itemOrId: restored,
                    options: { isLoading: false, isUpdating: false },
                  });
                },
                onOptimisticLocalUpdate: options.onOptimisticLocalUpdate,
                onEnriched: options.onEnriched,
                onEnrichFailed: options.onEnrichFailed,
              }),
            );
          } catch (error) {
            // Same 422 recovery as the enrich path: a skipped-enrichment PUT
            // used to leave a stale updated_at, so the next save 422'd too.
            // Pass `written` only when a newer edit already replaced it, so an
            // owned failure still resets after the local revert.
            return recoverFromRaceCondition(
              error,
              written.updated_at,
              newerEditOwnsStore ? written : undefined,
            );
          }
        }, errorHandlerOpts(prevProject));
      }

      // Capture before the optimistic update — after it, getProject()
      // returns the unenriched project and enrichProject would short-circuit.
      // No recipe catalog on purpose — see getEnrichmentOptions, it is export-only.
      const enrichOptions: EnrichProjectOptions = {
        ...getEnrichmentOptions(),
        ...getEnrichReuseOptions(project, options),
      };

      // Optimistic: apply unenriched project to store immediately so the
      // user's edit is visible before the worker finishes enrichment.
      const optimisticProject = updateProjectLocally({
        action: UpdateClientAction.Update,
        itemOrId: { ...project, enriched: false },
        options: {
          isLoading: !options.skipServerUpdate,
          isUpdating: !options.skipServerUpdate,
        },
      });

      const selectedVersionId = getSelectedVersionId();
      // Record undo before await so Cmd+Z works while enrich runs (useInitUndoRedo skips unenriched commits).
      updateUndoRedoStates(
        { project: prevProject, selectedVersionId },
        { project: optimisticProject, selectedVersionId },
      );

      options.onOptimisticLocalUpdate?.();

      const enrichResult = await tryCatchAwaitable(
        enrichOnWorkerCancellable(optimisticProject, enrichOptions),
      );
      // Stale: store was mutated while the worker was running
      if (getProject() !== optimisticProject) {
        options.onEnrichmentSuperseded?.();
        return getProject();
      }
      // Worker cooperatively cancelled (sequence bump) but store may still match optimistic ref.
      if (enrichResult.ok && enrichResult.value === null) {
        options.onEnrichmentSuperseded?.();
        return getProject();
      }

      // The worker returns a structured clone — its updated_at is a snapshot
      // from before the worker ran. A prior server response may have advanced
      // the timestamp via silentlySetUpdatedAt while the worker was running.
      if (enrichResult.ok && enrichResult.value) {
        enrichResult.value.updated_at = getProject().updated_at;
      }

      if (!enrichResult.ok) {
        // A newer edit already replaced this snapshot; reverting would wipe it.
        if (getProject() !== optimisticProject) {
          options.onEnrichmentSuperseded?.();
          return getProject();
        }
        // Revert first so onEnrichFailed sees the pre-edit project (URL restore).
        updateProjectLocally({
          action: UpdateClientAction.Update,
          itemOrId: prevProject,
          options: { isLoading: false, isUpdating: false },
        });
        options.onEnrichFailed?.();
        return errorHandler(enrichResult.error, errorHandlerOpts(prevProject));
      }

      /**
       * Enriched project document returned by {@link enrichOnWorkerCancellable} when `enrichResult.ok` is true.
       * Can be `undefined` if the worker reported success but yielded no payload — treated as enrichment failure below.
       */
      const enrichedProject = enrichResult.value;
      if (!enrichedProject) {
        options.onEnrichFailed?.();
        return getProject();
      }

      // Surface stored changes the pipeline deactivated for stale references (DEV-3640)
      reportInactiveProposalChanges(enrichedProject);

      // Local-only updates should not persist to the server. Note: soft-deleted
      // (isDeleted) elements must NOT block persistence — a legacy temp-element
      // guard here silently dropped every save for projects that leaked such
      // elements into a baseline tree (DEV-3641).
      if (options.skipServerUpdate) {
        const nextProject = updateProjectLocally({
          action: UpdateClientAction.Update,
          itemOrId: enrichedProject,
          options: { isLoading: false, isUpdating: false },
        });

        syncUndoRedoStatesAfterLocalEnrichedCommit(
          prevProject,
          nextProject,
          getSelectedVersionId(),
        );
        options.onEnriched?.();
        options.onPersisted?.();
        return nextProject;
      }

      const locallyCommitted = updateProjectLocally({
        action: UpdateClientAction.Update,
        itemOrId: enrichedProject,
        options: {
          isLoading: !options.skipServerUpdate,
          isUpdating: !options.skipServerUpdate,
        },
      });

      syncUndoRedoStatesAfterLocalEnrichedCommit(
        prevProject,
        locallyCommitted,
        getSelectedVersionId(),
      );
      options.onEnriched?.();

      const serverPutOptions = {
        ...pick(options, 'skipServerUpdate', 'skipEnrichment', 'onPersisted'),
        serverOnly: true as const,
      };

      return asyncErrorHandler(async () => {
        try {
          return captureReplayedSave(
            await updateProject(locallyCommitted, serverPutOptions),
          );
        } catch (error) {
          const replayed = await replaySnapshotAfterRace(
            error,
            locallyCommitted,
            serverPutOptions,
          );
          if (replayed) return replayed;
          return recoverFromRaceCondition(
            error,
            locallyCommitted.updated_at,
            locallyCommitted,
          );
        }
      }, errorHandlerOpts(getProject()));
    },
    [
      asyncErrorHandler,
      enqueueSnackbar,
      errorHandler,
      readonly,
      updateProject,
      updateProjectLocally,
    ],
  );
  return persistProjectUpdate;
};

/**
 * Creates a project in {@link organization} and opens its design view.
 * Returns undefined when no organization is selected.
 */
export const useCreateProject = (organization: string | undefined) => {
  const { createProject } = useProjectState('createProject');

  const promiseSnackbar = usePromiseSnackbar();
  const navigate = useNavigate();
  const logEvent = useLogEditProjectDialog();

  const createProjectFn = useCallback(
    async (
      payload: CreateProjectPayload,
      snackbarOptions?: ISnackbarOptions,
    ) => {
      const project = await promiseSnackbar(
        async () => {
          const created = await createProject(payload, organization);
          // Absolute design URL — useProjectNavigation can no-op or return to
          // the list when the current route has no projectId (DEV-3551).
          if (!created.template) {
            navigate(
              requireCreatedProjectDesignPath(
                organization,
                created.id,
                created.active_version_id,
              ),
            );
          }
          return created;
        },
        {
          errorMessage: 'Failed to create project',
          successMessage: 'New project created',
          logError: true,
          ...snackbarOptions,
        },
      );

      // log event to amplitude
      logEvent(payload);

      // expand parent folder in Project Selector
      if (project.parent_id) {
        setElementExpanded(project.parent_id, true, true);
      }

      return project;
    },
    [createProject, logEvent, navigate, promiseSnackbar, organization],
  );
  return !organization ? undefined : createProjectFn;
};

export const useDuplicateElement = () => {
  const addElement = useAddElement();
  return useCallback(
    async <T extends OneOfChildElements>(
      element: T,
      options: Pick<
        IAddElementClientOptions,
        'skipServerUpdate' | 'skipEnrichment'
      > = {},
    ) => {
      const { element: createdElement, project } = await addElement(
        element,
        element,
        {
          ...options,
          isSibling: true,
          placement: 'after',
          duplicateSubtreeSourceElementId: element.id,
        },
      );
      if (createdElement.kind !== element.kind)
        throw new Error('Element kind mismatch');

      return { element: createdElement as CreatedElement<T['kind']>, project };
    },
    [addElement],
  );
};

const defaultOptions: ISnackbarOptions = {
  errorMessage: 'Failed to delete project',
  successMessage: 'Project deleted',
  logError: true,
};
export const useDeleteProject = () => {
  const { deleteProject, projectsLookup } = useProjectState(
    'deleteProject',
    'projectsLookup',
  );
  const uiOrganization = useSelectedOrganizationName();
  const navigate = useProjectNavigation();
  const confirm = useIsConfirmed();
  const promiseSnackbar = usePromiseSnackbar();

  return useCallback(
    async (
      projectId: ProjectID,
      snackbarOptions: ISnackbarOptions,
    ): Promise<ProjectID | undefined> => {
      const project = required(projectsLookup[projectId]);

      const confirmed = await confirm({
        title: `Delete ${project.name}?`,
        confirmationText: 'Delete',
        content: `This will delete the project ${
          project.template ? 'template' : ''
        } and all its content. This action cannot be undone.`,
      });

      // If user cancels, don't delete project
      if (!confirmed) return;

      return promiseSnackbar(
        async () => {
          const selectedProject = getProject();

          // Delete project
          const deletedId = await deleteProject(projectId);
          amplitudeLog(AMP_PROJECT_DELETE, { ProjectId: projectId });
          // If deleted project is still the current project, navigate to home
          if (selectedProject.id === deletedId) {
            navigate({ organization: uiOrganization });
          }

          return deletedId;
        },
        {
          ...defaultOptions,
          ...snackbarOptions,
        },
      );
    },
    [
      confirm,
      uiOrganization,
      deleteProject,
      navigate,
      projectsLookup,
      promiseSnackbar,
    ],
  );
};

export function useAddElement(): UseAddElementFn {
  const applyChanges = useApplyProjectChanges();
  const { setAddedElementId, setSelectedElementId } = useUIState(
    'setAddedElementId',
    'setSelectedElementId',
  );
  const navigateTo = useProjectNavigation();

  const addElementFn: UseAddElementFn = useCallback(
    async <T extends OneOfChildFactoryElements>(
      target: OneOfElements,
      defaults: T,
      {
        placement = 'first',
        isSibling = false,
        navigate = true,
        applyOptions,
        regenerateIds,
        skipServerUpdate,
        skipEnrichment,
        duplicateSubtreeSourceElementId,
        propertyValues,
      }: IAddElementClientOptions = {},
    ) => {
      const version = getSelectedVersion();
      if (!version) {
        throw new Error(StoreErrorMessage.VersionUndefined);
      }

      const siblingId =
        isSibling && !isBuildingVersionElement(target) ? target.id : undefined;
      const parent = getParent(version, target, isSibling);
      const seeded = applyPropertyValuesToFactory(defaults, propertyValues);
      reportInvalidPropertyValues(
        seeded.invalid,
        getElementCategoryId(defaults),
      );
      // Pre-generate the new element id so we can drive UI selection / navigation before
      // the change is applied. Children inside the factory still get fresh ids when
      // `regenerateIds` requests it (handled by `preserveRootElementIds`).
      const newId = createId(defaults, regenerateIds);
      const factory = createFactoryWithId(seeded.defaults, newId);
      const factoryRegenerateIds = applyRegenerateIdsOptions(regenerateIds, {
        preserveRootElementIds: true,
      });

      if (navigate) {
        // Set immediately so the element renders as selected on first paint,
        // before the router roundtrip updates the UI store.
        setSelectedElementId(newId);
        navigateTo({ elementId: newId });
      }

      if (!isDeletedFactory(factory)) {
        setAddedElementId(newId);
      }

      const change: ProjectChange = {
        type: ProjectChangeType.ElementAdd,
        elementId: parent.id,
        factory,
        placement,
        siblingId,
        regenerateIds: factoryRegenerateIds,
        ...(duplicateSubtreeSourceElementId
          ? { duplicateSubtreeSourceElementId }
          : {}),
      };

      const applyResult = await applyChanges([change], {
        ...applyOptions,
        skipServerUpdate,
        skipEnrichment,
      });

      if (applyResult.cancelled) {
        throw new Error('User cancelled');
      }

      const element = findAddedElement<T>(
        applyResult.project,
        newId,
        defaults.kind,
      );
      if (!element) {
        throw new Error('Element was not added');
      }

      return { element, project: applyResult.project };
    },
    [applyChanges, navigateTo, setAddedElementId, setSelectedElementId],
  );
  return addElementFn;
}

/**
 * Locate the freshly added element by id in the updated {@link Project}, narrowing the
 * return type to the kind requested by the caller.
 */
const findAddedElement = <T extends OneOfChildFactoryElements>(
  project: Project,
  id: IElementID,
  kind: T['kind'],
): CreatedElement<T['kind']> | undefined => {
  const found = findElement(project, id);
  if (!isCreatedElementKind(found, kind)) return undefined;
  return found;
};

/**
 * Removes undefined factory fields and injects the pre-generated id while preserving
 * the discriminant so {@link ProjectChangeType.ElementAdd} can narrow the union.
 */
const createFactoryWithId = (
  defaults: OneOfChildFactoryElements,
  id: IElementID,
): Partial<OneOfChildFactoryElements> => {
  if (defaults.kind === ElementKind.Product) {
    return { ...omitUndefined(defaults), kind: ElementKind.Product, id };
  }
  return { ...omitUndefined(defaults), kind: ElementKind.Element, id };
};

const isCreatedElementKind = <K extends OneOfChildFactoryElements['kind']>(
  element: OneOfElements | undefined,
  kind: K,
): element is CreatedElement<K> => element?.kind === kind;

/**
 * Mirrors {@link isDeletedElement} for a factory partial — only {@link IElement}s can carry
 * `isDeleted: true`; product elements are never deleted in this sense.
 */
const isDeletedFactory = (
  factory: Partial<OneOfChildFactoryElements>,
): boolean => factory.kind === ElementKind.Element && !!factory.isDeleted;

export function useAddStandardElement(): UseAddStandardElementFn {
  const addElement = useAddElement();

  return useCallback(
    async (target, categoryId, options) => {
      // Expand with force = true to make sure we open even if the target is empty right now
      const expand = !options?.isSibling;
      setElementExpanded(target, expand, false, true);

      const defaults: IFactoryElement = {
        kind: ElementKind.Element,
        category_id: categoryId,
      };

      const { element, project } = await addElement(target, defaults, options);
      amplitudeLog(AMP_ELEMENT_ADD, { ElementId: element.id });
      return { element, project };
    },
    [addElement],
  );
}

/**
 * Adds a new {@link Baseline} to the building, optionally based on `partial` (pass an
 * existing version to duplicate it — child ids are always regenerated).
 *
 * The id is generated up front so the change and the mandatory active-version update
 * reference the same version in one commit. Every newly created version becomes
 * {@link Project.active_version_id}; undo reverts both together.
 * Navigation, first-proposal selection, and the optional rename hint run when the version is written locally
 * ({@link IUpdateProjectOptions.onOptimisticLocalUpdate}), without waiting for the server
 * PUT. A cancelled confirm never writes, so the URL is left alone. If persist or
 * enrich reverts the store, {@link IUpdateProjectOptions.onEnrichFailed} restores the URL
 * (store already reverted). Outcome after apply is read from the live store.
 *
 * @returns {@link AddVersionResult} — never throws for a cancelled or failed add.
 */
export function useAddVersion(): UseAddVersionFn {
  const applyChanges = useApplyProjectChanges();
  const navigateTo = useProjectNavigation();
  const { setRenamingId } = useUIState('setRenamingId');

  return useCallback(
    async (
      partial: Partial<IFactoryVersion> = {},
      {
        navigate = true,
        hintRename = true,
        duplicateNameBase,
        applyOptions,
      }: IAddVersionOptions = {},
    ) => {
      const versionId = createId({});
      const versions = getBuilding(getProject()).baselines;
      const versionName = duplicateNameBase
        ? getDuplicateVersionName(versions, duplicateNameBase)
        : getNextVersionName(versions);
      const previousVersionId = getSelectedVersion()?.id;
      const previousProposalSearchValue =
        getSelectedProposalSearchValueFromUrl();
      const change: ProjectChange = {
        type: ProjectChangeType.VersionAdd,
        versionId,
        ...(duplicateNameBase ? { duplicateNameBase } : {}),
        // Stamp the final automatic name before the optimistic/animation layers
        // can observe the factory. The mutator uses the same helper again against
        // its actual project snapshot, so the persisted result stays authoritative.
        factory: { ...partial, name: versionName },
      };

      const stamped = stampApplyAmplitudeVersionId(applyOptions, versionId);
      const applyOptionsWithVersionSnackbar: ApplyProjectChangesOptions = {
        snackbar: {
          type: 'undo',
          message: NEW_VERSION_CREATED_SNACKBAR_MESSAGE,
        },
        ...stamped,
      };

      const restoreAddUrl = () => {
        dispatchOptimisticVersionAddCancelled(versionId);
        if (!navigate) return;
        const restoreId = getVersionIdToRestoreAfterFailedAdd(
          versionId,
          previousVersionId,
          getProject(),
        );
        if (!restoreId) return;
        navigateTo(
          { versionId: restoreId, elementId: URL_CATEGORY_NONE },
          {
            searchParams: {
              [PROPOSAL_SEARCH_PARAM]: previousProposalSearchValue ?? null,
            },
          },
        );
        setRenamingId(undefined);
      };

      const applyResult = await applyChanges([change], {
        ...applyOptionsWithVersionSnackbar,
        // Same commit as the add, so a single undo drops both the new version
        // and its promotion to active version.
        activeVersionId: versionId,
        onOptimisticLocalUpdate: () => {
          if (navigate) dispatchOptimisticVersionAdded(versionId);
          stamped?.onOptimisticLocalUpdate?.();
          if (!navigate) return;
          const firstProposalId = getFirstProposalIdForVersion(
            getProject(),
            versionId,
          );
          navigateTo(
            { versionId, elementId: URL_CATEGORY_NONE },
            {
              searchParams: {
                [PROPOSAL_SEARCH_PARAM]: firstProposalId ?? null,
              },
            },
          );
          if (!hintRename) return;
          // `renamingId` is not URL-synced, so path sync cannot drop this
          // before the new version is selected (unlike `addedElementId`).
          setRenamingId(versionId);
        },
        onEnrichFailed: () => {
          stamped?.onEnrichFailed?.();
          restoreAddUrl();
        },
      });

      if (applyResult.cancelled) {
        return { status: 'cancelled', project: applyResult.project };
      }

      const liveProject = getProject();
      const element = getBuildingVersionById(liveProject, versionId);
      if (!element) {
        dispatchOptimisticVersionAddCancelled(versionId);
        return { status: 'failed', project: liveProject };
      }

      return { status: 'added', element, project: liveProject };
    },
    [applyChanges, navigateTo, setRenamingId],
  );
}

export function useAddProductElement(): UseAddProductElementFn {
  const addElement = useAddElement();
  const [, addUsedProductId] = useUsedProductIds();

  return useCallback(
    async (
      target: OneOfElements,
      defaults: IFactoryProductElement,
      isSibling = false,
    ) => {
      const version = getSelectedVersion();
      const { product_id, unit } = defaults;
      const productLookup = getProductsLookup();

      if (!product_id) {
        throw new Error('Product ID is required');
      }

      const product = getProductByIdDeprecated(
        productLookup,
        version,
        product_id,
      );

      // Make sure unit can be used to get a value from the conversion factors else use the product unit
      defaults.unit =
        unit && getConversionFactorValue(product, unit) ? unit : product.unit;

      const { element, project } = await addElement(
        target,
        {
          ...defaults,
          kind: ElementKind.Product,
        },
        { isSibling },
      );

      addUsedProductId(product_id);

      return { element, project };
    },
    [addElement, addUsedProductId],
  );
}

/**
 * Removes child elements ({@link IElement} / {@link IProductElement}) from the selected
 * version — hard on baseline, soft (proposal action) when a proposal is selected.
 * Whole building versions are removed with {@link useRemoveVersion} instead.
 */
export function useRemoveElements({
  showConfirm = false,
  navigate = true,
  skipServerUpdate,
  onDeleted,
}: IRemoveElementsOptions = {}): UseRemoveElementsFn {
  const applyChanges = useApplyProjectChanges();
  const navigateTo = useProjectNavigation();
  const selectedProposalId = useSelectedProposalId();

  return useCallback(
    async (...elementIds: IElementID[]) => {
      const [firstElementId] = elementIds;
      if (firstElementId === undefined) {
        throw new Error('No elements to remove');
      }

      const project = getProject();
      const selectedVersion = getSelectedVersion();
      const selectedElementId = getSelectedElementId();

      if (!selectedVersion) {
        throw new Error(StoreErrorMessage.VersionUndefined);
      }

      const names = getElementNames(selectedVersion, ...elementIds);

      const hasSelectedProposal =
        selectedProposalId !== undefined &&
        Boolean(
          selectedVersion.proposals?.some(
            (proposal) => proposal.id === selectedProposalId,
          ),
        );

      // Legacy EPD-picker element must leave the tree entirely — never proposal-soft-delete.
      const temporaryIds = elementIds.filter(isTemporaryElementId);
      const persistedIds = elementIds.filter((id) => !isTemporaryElementId(id));

      const removalChanges: ProjectChange[] = [
        ...temporaryIds.map((elementId): ProjectChange => ({
          type: ProjectChangeType.ElementRemove,
          elementId,
        })),
        ...(hasSelectedProposal
          ? // Proposal deletes are soft (baseline tree intact, action on the proposal),
            // except for elements this proposal created itself, which go for real.
            // Import bookkeeping follows the hard deletes only (DEV-3702).
            buildRemoveElementsInProposalChanges(
              selectedVersion,
              selectedProposalId,
              persistedIds,
            )
          : [
              // Baseline deletes are hard: remove from version.elements for all proposals.
              ...persistedIds.map((elementId): ProjectChange => ({
                type: ProjectChangeType.ElementRemove,
                elementId,
              })),
              ...buildDiscardedRowIdsChangesForElementRemoval(
                selectedVersion,
                persistedIds,
              ),
            ]),
      ];

      let deletionCommitted = false;
      const applyResult = await applyChanges(removalChanges, {
        skipServerUpdate,
        confirm: getRemoveElementsConfirmOptions(showConfirm, names),
        onPersisted: () => {
          deletionCommitted = true;
        },
      });

      if (applyResult.cancelled) return project;

      const updatedProject = applyResult.project;

      // Only navigate / notify after persistence succeeded — otherwise error-handler reverts
      // can leave undo snackbars and URLs inconsistent with the restored project.
      // Current element was deleted
      if (
        deletionCommitted &&
        navigate &&
        selectedElementId &&
        elementIds.includes(selectedElementId)
      ) {
        navigateTo({ elementId: URL_CATEGORY_NONE });
      }
      if (deletionCommitted) {
        onDeleted?.(names, firstElementId);
      }
      return updatedProject;
    },
    [
      applyChanges,
      navigate,
      navigateTo,
      onDeleted,
      selectedProposalId,
      showConfirm,
      skipServerUpdate,
    ],
  );
}

const getRemoveElementsConfirmOptions = (
  showConfirm: IRemoveElementsOptions['showConfirm'],
  names: string,
): ApplyProjectChangesConfirmOptions | undefined => {
  if (!showConfirm) return undefined;
  if (guards.is.string(showConfirm)) return showConfirm;

  const defaults = {
    title: `Delete ${names}`,
    description: `Are you sure you want to delete ${names}?`,
    confirmationText: 'Delete',
  };

  if (showConfirm === true) return defaults;
  return { ...defaults, ...showConfirm };
};

/** Confirm copy for deleting one version from the switcher. */
export const getRemoveVersionConfirmOptions = (
  versionName: string,
): Exclude<ApplyProjectChangesConfirmOptions, string> => {
  const options = getRemoveElementsConfirmOptions(true, versionName);
  if (!options || guards.is.string(options)) {
    return {
      title: `Delete ${versionName}`,
      description: `Are you sure you want to delete ${versionName}?`,
      confirmationText: 'Delete',
    };
  }
  return options;
};

/**
 * Removes a whole {@link Baseline} from the building via
 * {@link ProjectChangeType.VersionRemove}. A version delete is always hard — it is never
 * recorded as a proposal action since the proposals go with it.
 *
 * When the removed version was the selected one we open the immediately
 * preceding remaining version as soon as it is gone from the local store — not
 * after the server PUT. If persist or enrich reverts the delete, the URL is restored
 * in {@link IUpdateProjectOptions.onEnrichFailed} as soon as the store is reverted.
 *
 * Per-call {@link ApplyProjectChangesOptions} (e.g. amplitude) merge into the apply so a
 * cancelled confirm is not counted as a delete.
 */
export function useRemoveVersion({
  showConfirm = false,
  navigate = true,
  selectedVersionId: selectedVersionIdOverride,
  orderedVersionIds,
  applyOptions: hookApplyOptions,
}: IRemoveVersionOptions = {}): UseRemoveVersionFn {
  const applyChanges = useApplyProjectChanges();
  const navigateTo = useProjectNavigation();
  const routeParams = useRawProjectParams();
  const routeVersionId = routeParams.data.versionId;

  return useCallback(
    async (versionId: IElementID, callApplyOptions) => {
      const project = getProject();
      const version = getBuildingVersionById(project, versionId);
      if (!version) {
        throw new Error(StoreErrorMessage.VersionUndefined);
      }
      const wasSelected = shouldNavigateAfterVersionRemove(
        versionId,
        routeVersionId,
        selectedVersionIdOverride ?? getSelectedVersion()?.id,
      );
      const previousProposalSearchValue =
        getSelectedProposalSearchValueFromUrl();
      const versionDeleteNavigation = getVersionDeleteNavigation(
        orderedVersionIds ??
          getAllBuildingVersions(project).map(
            (buildingVersion) => buildingVersion.id,
          ),
        versionId,
        {
          routeVersionId,
          selectedVersionId:
            selectedVersionIdOverride ?? getSelectedVersion()?.id,
        },
      );
      const nextSelectedVersionId = versionDeleteNavigation?.versionId;

      const change: ProjectChange = {
        type: ProjectChangeType.VersionRemove,
        versionId,
      };

      const mergedApply = { ...hookApplyOptions, ...callApplyOptions };

      const restoreRemoveUrl = () => {
        if (!navigate || !wasSelected) return;
        const restoreId = getVersionIdToRestoreAfterFailedRemove(
          versionId,
          getProject(),
        );
        if (!restoreId) return;
        navigateTo(
          {
            versionId: restoreId,
            elementId: URL_CATEGORY_NONE,
          },
          {
            searchParams: {
              [PROPOSAL_SEARCH_PARAM]: previousProposalSearchValue ?? null,
            },
          },
        );
      };

      const applyResult = await applyChanges([change], {
        ...mergedApply,
        confirm: getRemoveElementsConfirmOptions(showConfirm, version.name),
        onOptimisticLocalUpdate: () => {
          mergedApply.onOptimisticLocalUpdate?.();
          if (!navigate || !wasSelected) return;
          const nextProject = getProject();
          const nextVersionId = nextSelectedVersionId;
          if (!nextVersionId) return;
          const nextVersion = getBuildingVersionById(
            nextProject,
            nextVersionId,
            false,
          );
          if (!nextVersion) return;
          const proposalIds = nextVersion.proposals?.map(({ id }) => id) ?? [];
          const destinationProposalId = getRememberedProposalForVersion(
            nextProject.id,
            nextVersionId,
            proposalIds[0],
            proposalIds,
          );
          navigateTo(
            {
              versionId: nextVersionId,
              elementId: URL_CATEGORY_NONE,
            },
            {
              flushSync: versionDeleteNavigation?.flushSync,
              searchParams: {
                [PROPOSAL_SEARCH_PARAM]: destinationProposalId ?? null,
              },
            },
          );
        },
        onEnrichFailed: () => {
          mergedApply.onEnrichFailed?.();
          restoreRemoveUrl();
        },
      });

      if (applyResult.cancelled) return project;

      return getProject();
    },
    [
      applyChanges,
      hookApplyOptions,
      navigate,
      navigateTo,
      orderedVersionIds,
      routeVersionId,
      selectedVersionIdOverride,
      showConfirm,
    ],
  );
}

/**
 * Update elements in project.
 * Also updates product records in project.
 * @returns
 */
export function useUpdateElements(
  options: Omit<SnapshotProjectUpdate, 'kind' | 'project'> = {},
): UseUpdateElementsFn {
  const updateProject = useUpdateProject();
  const memoOptions = useObjectMemo(options);

  return useCallback(
    async (...elementChanges) => {
      const updatedProject = updateElements(getProject(), ...elementChanges);

      return await updateProject({
        kind: 'snapshot',
        project: updatedProject,
        ...memoOptions,
      });
    },
    [memoOptions, updateProject],
  );
}

const getParent = (
  version: Baseline,
  element: OneOfElements,
  isSibling: boolean,
): OneOfParentElements => {
  if (isBuildingVersionElement(element)) {
    return version;
  }
  return getParentOfOneOfElementListChildren(element, isSibling);
};

const getParentOfOneOfElementListChildren = (
  element: OneOfElements,
  isSibling: boolean,
): OneOfParentElements => {
  if (isSibling || !isElement(element)) {
    const parent = getParentElement(getProject(), element);

    if (!parent) {
      throw new Error('Parent element not found');
    }
    return parent;
  }
  return element;
};

/**
 * Collect store snapshots needed by the enrichment pipeline.
 * Captured on the main thread before dispatching to the worker.
 *
 * @param recipes - **Export only** — see {@link EnrichProjectOptions.recipes}. Pass the store
 *   catalog when the enriched document must be self-contained (project JSON, Boverket XML,
 *   spreadsheet), i.e. through {@link getEnrichedProject}. Interactive enrichment omits it:
 *   {@link getProductsLookup} already covers everything compute reads, and the catalog would only
 *   grow the worker postMessage. Anything that needs recipes for correctness cannot rely on this.
 */
const getEnrichmentOptions = (recipes?: Recipe[]): EnrichProjectOptions => ({
  prevProject: getProject(),
  productRecord: getProductsLookup(),
  recipes,
  weatherHours: getPvgisState().weatherData?.environmentHourData,
  dataTables: getDataTables(),
});

/**
 * Enrich a project via the web worker.
 * Callers are responsible for their own staleness checks.
 */
export const getEnrichedProject = (
  project: Project,
  recipes?: Recipe[],
): Promise<Project> => enrichOnWorker(project, getEnrichmentOptions(recipes));

/**
 * Admin escape hatch: re-run the whole enrich pipeline on the current project
 * without any reuse — no `prevProject` diff, no {@link EnrichScope}, so every
 * version, proposal and results record is recomputed.
 *
 * Commits through {@link useUpdateProject}, so the result is persisted and an
 * undo/redo frame is pushed whenever the recomputed project differs from the
 * current one (a no-op enrichment leaves the stacks untouched).
 */
export const useForceEnrichProject = (): (() => Promise<Project>) => {
  const updateProject = useUpdateProject();

  return useCallback(
    () =>
      updateProject({
        kind: 'snapshot',
        // Fresh ref: useUpdateProject returns early on an identical reference.
        project: { ...getProject() },
        forceEnrichment: true,
      }),
    [updateProject],
  );
};

/**
 * Warning!:
 *
 * Do not use async states from this hook to take decisions on if to fetch
 * things if you have provided a verifyOrg.
 *
 * This is because the projects store doesn't necessarily know which org it will fetch projects from at fetch time,
 * and we cannot know async state in this case. Therefore we will just return false and null for those properties
 *
 * @param verifyOrg - the organization to verify the projects are from.
 * @returns projects sorted by last updated, with async state
 */
export const useSortedByLastUpdatedProjects = (
  verifyOrg?: string,
): {
  isFetching: boolean;
  isFetched: boolean;
  error: Error | null;
  projects: IProjectInfo[];
} => {
  const {
    projectsLookup,
    projectsOrg,
    projectsFetching,
    projectsFetched,
    projectsError,
  } = useProjectState(
    'projectsFetched',
    'projectsFetching',
    'projectsError',
    'projectsLookup',
    'projectsOrg',
  );

  return useMemo(() => {
    if (verifyOrg && projectsOrg !== verifyOrg) {
      // Lookup belongs to another org, or the first fetch for this org failed.
      return {
        isFetching: false,
        isFetched: false,
        error: projectsError ?? null,
        projects: [],
      };
    }
    const projects = Object.values(projectsLookup);

    return {
      isFetching: projectsFetching,
      isFetched: projectsFetched,
      error: projectsError ?? null,
      projects: sortProjectsByLastUpdated(projects),
    };
  }, [
    projectsLookup,
    projectsOrg,
    verifyOrg,
    projectsFetching,
    projectsFetched,
    projectsError,
  ]);
};

/**
 * Largest legacy project target among overview projects.
 * Templates are excluded so they cannot stretch list bars.
 */
export const useProjectsMaxTarget = (): number => {
  const projects = useProjectsForOverview();
  return useMemo(
    () => Math.max(...projects.map(({ target }) => target ?? 0)),
    [projects],
  );
};

/**
 * Per-indicator maximum used to scale list result bars.
 * Templates are excluded.
 */
export const useProjectListMaxResults = (): Results => {
  const projects = useProjectsForOverview();
  const getResultsPerGFA = useGetResultsPerGFA();
  const filterResults = useFilterProjectListResults();

  return useMemo(
    () =>
      getMaxValuesInArray(
        projects.map(({ results, gfa }) =>
          getResultsPerGFA(filterResults(results ?? {}), gfa),
        ),
      ),
    [projects, getResultsPerGFA, filterResults],
  );
};

/**
 * Organization-wide CO2 and cost maxima for map-card comparison bars.
 * Templates are excluded.
 */
export const useProjectListComparisonMaxima = () => {
  const projects = useProjectsForOverview();
  const getResultsPerGFA = useGetResultsPerGFA();
  const filterResults = useFilterProjectListResults();
  return useMemo(
    () =>
      projects.reduce(
        (max, project) => {
          const proposal = getResultsPerGFA(
            filterResults(project.results ?? {}),
            project.gfa,
          );
          const baseline = getResultsPerGFA(
            filterResults(project.baseline_results ?? {}),
            project.gfa,
          );
          return {
            co2e: Math.max(
              max.co2e,
              getConversionFactorValue(proposal, 'co2e'),
              getConversionFactorValue(baseline, 'co2e'),
            ),
            cost: Math.max(max.cost, getConversionFactorValue(proposal, 'sek')),
          };
        },
        { co2e: 0, cost: 0 },
      ),
    [projects, getResultsPerGFA, filterResults],
  );
};

/**
 * Largest GFA among overview projects, used to scale map cards.
 * Templates are excluded.
 */
export const useProjectListMaxGFA = (): number => {
  const projects = useProjectsForOverview();
  return useMemo(
    () => Math.max(...projects.map(({ gfa }) => gfa ?? 0)),
    [projects],
  );
};

export const useProjectListGFAScale = (
  gfa: number,
  min = 0.04,
  max = 1,
): number => {
  const maxGFA = useProjectListMaxGFA();
  const scale = gfa / maxGFA;
  return clamp(scale, min, max);
};

/**
 * @returns total amount of gross floor area in square meters for available projects,
 * excluding readonly projects
 */
export const useProjectsTotalGFA = (): number => {
  const { projectsLookup } = useProjectState('projectsLookup');

  return Object.values(projectsLookup).reduce(
    (acc, project) =>
      acc + (!project.archived && project.gfa ? project.gfa : 0),
    0,
  );
};

/**
 * Get the gross floor area (GFA) of the project from every building in the design.
 * @returns The gross floor area (GFA) of the project
 */
export const useProjectBuildingGFA = (): number => useProjectDesignGfa();

export const useExportProject = (): ((id: ProjectID) => Promise<void>) => {
  const [config] = useConfig();
  const confirm = useIsConfirmed();
  // Include soft-deleted recipes so referenced recipes are bundled in the export.
  const recipes = useRecipes(true);

  return useCallback(
    async (id) => {
      const project = await fetchProject(id);
      const recipeLookup = getRecipeLookup();
      const recipesForEnrich = recipes.length
        ? recipes
        : Object.values(recipeLookup);
      const recipesForExport = Object.values(recipeLookup);
      let projectToExport = project;

      try {
        projectToExport = await getEnrichedProject(project, recipesForEnrich);
        exportProject(projectToExport, recipesForExport, config);
      } catch (error) {
        if (!isProjectExportValidationError(error)) throw error;

        const confirmed = await confirm(
          getSkipProjectExportValidationConfirmOptions(error),
        );
        if (!confirmed) throw error;

        // Enrichment can throw before it returns. Export the last project we have.
        exportProject(projectToExport, recipesForExport, config, {
          skipValidation: true,
        });
      }
    },
    [config, confirm, recipes],
  );
};

export const useProjectRestriction = (): ((
  id: ProjectID,
  name: string,
  action: { lock?: boolean; archive?: boolean },
  shouldConfirm?: boolean,
) => Promise<void>) => {
  const confirm = useIsConfirmed();
  const promiseSnackbar = usePromiseSnackbar();
  const { updateProjectDetails } = useProjectState('updateProjectDetails');

  return useCallback(
    async (id, name, { lock, archive }, shouldConfirm = false) => {
      const isArchiveAction = archive !== undefined && !lock;

      const lockAction = lock ? 'lock' : 'unlock';
      const archiveAction = archive ? 'archive' : 'unarchive';
      const action = isArchiveAction ? archiveAction : lockAction;

      const confirmed = shouldConfirm
        ? await confirm({
            title: `${capitalize(action)} ${name}`,
            confirmationText: capitalize(action),
            description: getProjectRestrictionDescription(action),
          })
        : true;

      if (confirmed) {
        promiseSnackbar(
          updateProjectDetails({
            reference: id,
            data: { archived: archive, locked: lock },
          }),
          {
            successMessage: getProjectRestrictionSuccessMessage(action),
            errorMessage: `Failed to ${action} project`,
          },
        );
        amplitudeLog(RESTRICT_ACTION_EVENT[action], {
          ProjectId: id,
          ProjectName: name,
        });
      }
    },
    [confirm, promiseSnackbar, updateProjectDetails],
  );
};

type RestrictAction = 'lock' | 'unlock' | 'archive' | 'unarchive';

const RESTRICT_ACTION_DESCRIPTION: Record<RestrictAction, string> = {
  lock: 'Locked projects can only be edited by the project owner',
  archive: 'Archived projects cannot be edited by any user',
  unlock: 'The project will become possible to edit again',
  unarchive: 'The project will become possible to edit again',
};

const RESTRICT_ACTION_SUCCESS_MESSAGE: Partial<Record<RestrictAction, string>> =
  {
    lock: 'The project is locked and can only be edited by the owner (you)',
    unlock:
      'The project is unlocked and can be edited by anyone in your organisation',
  };

const RESTRICT_ACTION_EVENT: Record<RestrictAction, string> = {
  archive: AMP_PROJECT_ARCHIVE,
  unarchive: AMP_PROJECT_UNARCHIVE,
  lock: AMP_PROJECT_LOCK,
  unlock: AMP_PROJECT_UNLOCK,
};

const getProjectRestrictionDescription = (action: RestrictAction) =>
  RESTRICT_ACTION_DESCRIPTION[action];

const getProjectRestrictionSuccessMessage = (action: RestrictAction) =>
  RESTRICT_ACTION_SUCCESS_MESSAGE[action];

export const useUpdateProjectAndFolderLocations = () => {
  const { updateProjectAndFolderLocations } = useProjectState(
    'updateProjectAndFolderLocations',
  );

  return useCallback(
    (itemsToMove: OneOfProjectListElements[]) => {
      const modifiedIds = itemsToMove.map(getId);
      const unmodifiedFolders = getFolders().filter(
        (folder) => !modifiedIds.includes(folder.id),
      );

      validateFolderList([...unmodifiedFolders, ...itemsToMove]);

      return updateProjectAndFolderLocations(itemsToMove);
    },
    [updateProjectAndFolderLocations],
  );
};

export const useFindFreeProjectName = (): ((name?: string) => string) =>
  useCallback((name) => {
    const names = Object.values(getProjectsLookup()).map(
      (project) => project.name,
    );
    return findFreeName(names, name ?? 'Project 1');
  }, []);
